import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import Navigation from './Navigation';

const TradingInterface = () => {
  const [symbol, setSymbol] = useState('AAPL');
  const [betAmount, setBetAmount] = useState(10);
  const [targetGain, setTargetGain] = useState(20);
  const [timeframeMinutes, setTimeframeMinutes] = useState(30);
  const [calculatedPosition, setCalculatedPosition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showApproval, setShowApproval] = useState(false);

  const popularStocks = [
    { symbol: 'AAPL', name: 'Apple' },
    { symbol: 'GOOGL', name: 'Google' },
    { symbol: 'MSFT', name: 'Microsoft' },
    { symbol: 'TSLA', name: 'Tesla' },
    { symbol: 'AMZN', name: 'Amazon' },
    { symbol: 'META', name: 'Meta' },
    { symbol: 'NVDA', name: 'NVIDIA' },
    { symbol: 'NFLX', name: 'Netflix' }
  ];

  const timeframeOptions = [
    { minutes: 0.5, label: '30 seconds' },
    { minutes: 1, label: '1 minute' },
    { minutes: 5, label: '5 minutes' },
    { minutes: 15, label: '15 minutes' },
    { minutes: 30, label: '30 minutes' },
    { minutes: 60, label: '1 hour' },
    { minutes: 120, label: '2 hours' },
    { minutes: 240, label: '4 hours' }
  ];

  const calculatePosition = async () => {
    if (!symbol || betAmount <= 0 || targetGain <= 0 || timeframeMinutes <= 0) {
      toast.error('Please fill in all fields with positive values');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/trading/calculate-position', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          symbol,
          betAmount: parseFloat(betAmount),
          targetGain: parseFloat(targetGain),
          timeframeMinutes: parseFloat(timeframeMinutes)
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setCalculatedPosition(data.calculatedPosition);
        setShowApproval(true);
        toast.success('Position calculated successfully!');
      } else {
        toast.error(data.error || 'Failed to calculate position');
      }
    } catch (error) {
      toast.error('Error calculating position');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const placeBet = async () => {
    if (!calculatedPosition) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/trading/place-bet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          symbol,
          betAmount: parseFloat(betAmount),
          targetGain: parseFloat(targetGain),
          timeframeMinutes: parseFloat(timeframeMinutes),
          calculatedPosition
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('🎉 Bet placed successfully!');
        setShowApproval(false);
        setCalculatedPosition(null);
        // Reset form
        setBetAmount(10);
        setTargetGain(20);
        setTimeframeMinutes(30);
      } else {
        toast.error(data.error || 'Failed to place bet');
      }
    } catch (error) {
      toast.error('Error placing bet');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (riskScore) => {
    if (riskScore < 0.3) return 'text-green-400';
    if (riskScore < 0.6) return 'text-yellow-400';
    if (riskScore < 0.8) return 'text-orange-400';
    return 'text-red-400';
  };

  const getProbabilityColor = (probability) => {
    if (probability >= 70) return 'text-green-400';
    if (probability >= 50) return 'text-yellow-400';
    if (probability >= 30) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-white text-center mb-8">🎯 Create Your Bet</h1>
        
        {!showApproval ? (
          <div className="max-w-2xl mx-auto">
            {/* Bet Creation Form */}
            <div className="casino-card p-8 mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Set Your Parameters</h2>
              
              {/* Stock Selection */}
              <div className="mb-6">
                <label className="block text-gray-300 text-sm font-bold mb-2">
                  Choose Stock/Asset
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {popularStocks.map((stock) => (
                    <button
                      key={stock.symbol}
                      onClick={() => setSymbol(stock.symbol)}
                      className={`p-3 rounded-lg text-sm font-medium transition-all ${
                        symbol === stock.symbol
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {stock.symbol}
                      <div className="text-xs opacity-75">{stock.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Bet Amount */}
              <div className="mb-6">
                <label className="block text-gray-300 text-sm font-bold mb-2">
                  How much do you want to bet?
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={betAmount}
                    onChange={(e) => setBetAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="10"
                    min="1"
                    step="1"
                  />
                </div>
              </div>

              {/* Target Gain */}
              <div className="mb-6">
                <label className="block text-gray-300 text-sm font-bold mb-2">
                  How much do you want to win?
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={targetGain}
                    onChange={(e) => setTargetGain(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="20"
                    min="1"
                    step="1"
                  />
                </div>
              </div>

              {/* Timeframe */}
              <div className="mb-8">
                <label className="block text-gray-300 text-sm font-bold mb-2">
                  Timeframe
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {timeframeOptions.map((option) => (
                    <button
                      key={option.minutes}
                      onClick={() => setTimeframeMinutes(option.minutes)}
                      className={`p-3 rounded-lg text-sm font-medium transition-all ${
                        timeframeMinutes === option.minutes
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculatePosition}
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-4 px-6 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '🧠 Calculating Optimal Position...' : '🎯 Calculate My Position'}
              </button>
            </div>
          </div>
        ) : (
          /* Position Approval Interface */
          <div className="max-w-3xl mx-auto">
            <div className="casino-card p-8">
              <h2 className="text-2xl font-bold text-white mb-6">🎯 Position Calculated</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Your Bet Summary */}
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h3 className="text-lg font-bold text-white mb-4">Your Bet</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Asset:</span>
                      <span className="text-white font-bold">{symbol}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Bet Amount:</span>
                      <span className="text-white font-bold">${betAmount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Target Gain:</span>
                      <span className="text-green-400 font-bold">${targetGain}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Timeframe:</span>
                      <span className="text-white font-bold">
                        {timeframeOptions.find(t => t.minutes === timeframeMinutes)?.label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Calculated Position */}
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h3 className="text-lg font-bold text-white mb-4">AI Calculated Position</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Direction:</span>
                      <span className={`font-bold ${calculatedPosition?.direction > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {calculatedPosition?.direction > 0 ? 'Long ↗' : 'Short ↘'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Instrument:</span>
                      <span className="text-white font-bold capitalize">{calculatedPosition?.instrument}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Leverage:</span>
                      <span className="text-white font-bold">{calculatedPosition?.leverage?.toFixed(1)}x</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Current Price:</span>
                      <span className="text-white font-bold">${calculatedPosition?.currentPrice}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Quantity:</span>
                      <span className="text-white font-bold">{calculatedPosition?.tradingDetails?.quantity} shares</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Success Probability:</span>
                      <span className={`font-bold ${getProbabilityColor(calculatedPosition?.successProbability)}`}>
                        {calculatedPosition?.successProbability}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bracket Orders */}
              <div className="bg-gray-800 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-bold text-white mb-4">🎯 Bracket Orders Strategy</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h4 className="text-green-400 font-bold mb-2">Entry Order</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Type:</span>
                        <span className="text-white">{calculatedPosition?.bracketOrders?.entryOrder?.orderType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Side:</span>
                        <span className={`font-bold ${calculatedPosition?.bracketOrders?.entryOrder?.side === 'BUY' ? 'text-green-400' : 'text-red-400'}`}>
                          {calculatedPosition?.bracketOrders?.entryOrder?.side}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Quantity:</span>
                        <span className="text-white">{calculatedPosition?.bracketOrders?.entryOrder?.quantity}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h4 className="text-red-400 font-bold mb-2">Stop Loss</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Price:</span>
                        <span className="text-white">${calculatedPosition?.stopLossPrice}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Type:</span>
                        <span className="text-white">{calculatedPosition?.bracketOrders?.stopLoss?.orderType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Max Loss:</span>
                        <span className="text-red-400">${calculatedPosition?.tradingDetails?.maxLoss}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h4 className="text-green-400 font-bold mb-2">Take Profit</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Price:</span>
                        <span className="text-white">${calculatedPosition?.takeProfitPrice}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Type:</span>
                        <span className="text-white">{calculatedPosition?.bracketOrders?.takeProfit?.orderType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Target Gain:</span>
                        <span className="text-green-400">${calculatedPosition?.tradingDetails?.maxGain}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Risk Analysis */}
              <div className="bg-gray-800 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-bold text-white mb-4">Risk Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${getRiskColor(calculatedPosition?.riskScore)}`}>
                      {calculatedPosition?.riskScore ? (calculatedPosition.riskScore * 100).toFixed(0) : 0}%
                    </div>
                    <div className="text-gray-300 text-sm">Risk Score</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-400">
                      {calculatedPosition?.marketAnalysis?.modelConfidence}%
                    </div>
                    <div className="text-gray-300 text-sm">AI Confidence</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-400">
                      {calculatedPosition?.marketAnalysis?.currentSentiment}
                    </div>
                    <div className="text-gray-300 text-sm">Market Sentiment</div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => setShowApproval(false)}
                  className="flex-1 bg-gray-700 text-white font-bold py-4 px-6 rounded-lg hover:bg-gray-600 transition-all"
                >
                  ← Modify Bet
                </button>
                <button
                  onClick={placeBet}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold py-4 px-6 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? '🚀 Placing Bet...' : '✅ Approve & Place Bet'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingInterface;